import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AnyBet, editBetByField, showUserbets } from '../../../../services/admin/BetsService.tsx';
import { addBetToAvailable } from '../../../../services/admin/UserService.tsx';
import { addTransaction } from '../../../../services/admin/Transaction.tsx';
import { formatNumber, formatNumbers, formatWithSign, getAdminPendingSelectionLabel, getBetMatchupLabel, getBetPrimaryName, getLiveLabel, getPendingMarketSubLabel, isFreePlayBet, isPropsBet } from '../../../../helpers/functions.tsx';
import { Timestamp } from 'firebase/firestore';
import { formatAdminFiguresDateTime } from '../../../../helpers/adminFiguresTime.tsx';
import { FaSyncAlt } from 'react-icons/fa';
import { IoCloseSharp } from 'react-icons/io5';
import { MlbPitchersDetailRow } from '../../../shared/MlbPitchersDetailRow.tsx';
import BetLogo from '../../../player/modals/BetLogo.tsx';
import { useUser } from '../../../../context/UserContext.tsx';
import { logUserActivity } from '../../../../services/player/LoginService.tsx';
import { BetDetails } from '../../../../models/MyBetModel.tsx';
import '../../../../styles/admin/UsersPending.css';

type BetDateValue = Timestamp | { seconds?: number; toDate?: () => Date } | null | undefined;

const formatDateTime = (timestamp: BetDateValue): string => {
    if (!timestamp) return '';

    const date =
        typeof timestamp.toDate === 'function'
            ? timestamp.toDate()
            : timestamp.seconds != null
                ? new Date(timestamp.seconds * 1000)
                : null;

    if (!date || Number.isNaN(date.getTime())) return '';

    return formatAdminFiguresDateTime(date);
};

const capitalize = (text?: string) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

const isParlay = (bet: AnyBet) => 'selections' in bet;
const hasBetName = (bet: AnyBet): bet is BetDetails => 'betName' in bet;

const UserPending = () => {

    const { user } = useParams<{ user: string }>();
    const { user: currentUser, userIpData: userIpDataAdmin, setPendingBet } = useUser();

    const [formData, setFormData] = useState<AnyBet[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedPlayers, setExpandedPlayers] = useState<Set<string>>(() => new Set());
    const [selectedBet, setSelectedBet] = useState<AnyBet | null>(null);

    const { totalBetAmount, totalRewardAmount } = useMemo(() => {
        const bets = formData ?? [];
        return {
            totalBetAmount: bets.reduce((sum, bet) => isFreePlayBet(bet) ? sum : sum + (bet.bet || 0), 0),
            totalRewardAmount: bets.reduce((sum, bet) => sum + (bet.reward || 0), 0),
        };
    }, [formData]);

    useEffect(() => {
        if (!user) return;

        let cancelled = false;
        setIsLoading(true);
        setFormData(null);

        const loadPending = async () => {
            try {
                const result = await showUserbets(user);
                if (!cancelled) setFormData(result);
            } catch (error) {
                console.error('Error loading user pending bets:', error);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        loadPending();

        return () => { cancelled = true; };
    }, [user]);

    const confirmDelete = async () => {
        if (!selectedBet?.ticket || !selectedBet.player || selectedBet.bet === undefined) {
            alert('No player or bet selected');
            return;
        }

        try {
            const now = new Date();
            const gameDate = (selectedBet.gameDate as Timestamp | undefined)?.toDate?.() ?? now;
            const freePlay = isFreePlayBet(selectedBet);

            await editBetByField(selectedBet.ticket, {
                deleteBy: currentUser?.userName,
                dateDelete: now,
                descriptionDelete: now > gameDate
                    ? 'Bet removed after the match started'
                    : 'Bet removed before the match started',
            });

            await addBetToAvailable(
                selectedBet.player.userName,
                selectedBet.bet,
                freePlay ? { isFreePlay: true } : undefined,
            );

            await addTransaction({
                ticket: selectedBet.ticket,
                createDate: Timestamp.fromDate(now),
                agent: selectedBet.player.parent ?? '',
                customer: selectedBet.player.userName,
                transactionType: 'refund',
                description: `Refund - Ticket ${selectedBet.ticket}`,
                amount: selectedBet.bet,
                createBy: currentUser?.userName ?? '',
                userType: 'Player',
                transactionFree: freePlay,
                master: selectedBet.player.master,
            } as any);

            if (currentUser?.userName) {
                await logUserActivity(
                    currentUser.userName,
                    `bet eliminated: Ticket ${selectedBet.ticket} | Bet: ${selectedBet.betName}`,
                    userIpDataAdmin,
                );
            }

            alert('Bet deleted successfully');
            setFormData(prev => (prev ?? []).filter(bet => bet.ticket !== selectedBet.ticket));
            if (!freePlay) {
                setPendingBet(prev => prev?.filter(bet => bet.ticket !== selectedBet.ticket) ?? prev);
            }
            setSelectedBet(null);
        } catch (error) {
            console.error(error);
            alert('Error deleting bet');
        }
    };

    const togglePlayerDetails = useCallback((ticket: string) => {
        setExpandedPlayers(prev => {
            const next = new Set(prev);
            if (next.has(ticket)) next.delete(ticket);
            else next.add(ticket);
            return next;
        });
    }, []);

    const showSpinner = isLoading && formData === null;

    return (
        <>
            {showSpinner ? (
                <div className="loader-container">
                   <FaSyncAlt className="loader-icon-proyect" />
                </div>
            ) : (
                <div className='container-transaction mt-1'>
                    <div className='mt-3 d-flex justify-content-end'>
                        <span className="me-2 body-customer">
                            Risking: <span className='negative in-bold'>${formatNumbers(totalBetAmount)}</span>
                        </span>
                        <span className="me-2 body-customer">
                            To Win: <span>${formatNumbers(totalRewardAmount)}</span>
                        </span>
                    </div>
                    <div className="table-responsive p-1">
                        <table className="table">
                            <thead className="header-customer">
                                <tr>
                                    <th className='none team-color-pending'>
                                        <span>Accepted (PST)</span>
                                    </th>
                                    <th >
                                        <span>Description</span>
                                    </th>
                                    <th>
                                        <span>Risk</span>
                                    </th>
                                    <th className='none'>
                                        <span>To Win</span>
                                    </th>
                                    <th className='none'></th>
                                </tr>
                            </thead>
                            <tbody className="body-customer">
                                {formData?.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="text-center body-customer fw-bold">
                                            No pending bets
                                        </td>
                                    </tr>
                                ) : formData?.map((player: any) => {
                                    const expanded = expandedPlayers.has(player.ticket)

                                    const market = player.marketName ?? player.odd?.market ?? "";
                                    const price = player.odd?.price ?? 0;
                                    const points = player.odd?.points;
                                    const selectionLine = player.odd?.selection_line;
                                    const isParlay = player.betType === "Parlay" || player.betType === "Teaser";
                                    const isContestOrProps = player.betType === "Contest" || isPropsBet(player) || Boolean(getPendingMarketSubLabel(player));
                                    const liveLabel = getLiveLabel(player.gameType);


                                    return (
                                        <React.Fragment key={player.ticket}>
                                            <tr className="hover-row" onClick={() => togglePlayerDetails(player.ticket)}>
                                                <td className='none team-color-pending'>
                                                    {formatDateTime(player.betDate)}
                                                </td>
                                                <td className="text-left-important fixed-bet-column">
                                                    <span>
                                                        {isParlay ? (
                                                            <span>
                                                                <span className="description-text">{player.description}</span>
                                                                {!expanded && (
                                                                    <div className="info-descriptions">
                                                                        {player.selections?.map((selection: any, index: number) => {

                                                                            const sPrice = selection.odd?.price ?? 0
                                                                            const sPoints = selection.odd?.points

                                                                            return (
                                                                                <div key={index} className='d-flex align-items-center mt-1'>
                                                                                    <div>
                                                                                        {selection.logo && (
                                                                                            <BetLogo bet={selection} className="sport-logos-pending-player" alt="" />
                                                                                        )}
                                                                                    </div>
                                                                                    <div>
                                                                                        <span className="fw-bold team-color-pending">
                                                                                            {getBetPrimaryName(selection)}
                                                                                        </span>
                                                                                        <span className="bet-format">
                                                                                            {sPoints != null
                                                                                                ? `${formatWithSign(
                                                                                                    sPoints,
                                                                                                    selection.odd?.selection_line,
                                                                                                    "points"
                                                                                                )} `
                                                                                                : ""
                                                                                            }
                                                                                            {formatWithSign(sPrice)}
                                                                                        </span>&nbsp;
                                                                                        {selection.grader?.status === "Completed" && selection.grader?.result && (
                                                                                            <span
                                                                                                style={{
                                                                                                    color: selection.grader.result === "Won" ? "green" : "red",
                                                                                                    fontWeight: "bold",
                                                                                                }}>
                                                                                                {selection.grader.result === "Won" ? "W" : "L"}
                                                                                            </span>
                                                                                        )}&nbsp;
                                                                                        {getPendingMarketSubLabel(selection) && (
                                                                                            <span className='name-pending-props'>{getPendingMarketSubLabel(selection)}</span>
                                                                                        )}
                                                                                    </div>



                                                                                </div>
                                                                            )
                                                                        })}

                                                                    </div>
                                                                )}
                                                            </span>
                                                        ) : isContestOrProps ? (
                                                            <span className='d-flex'>
                                                                <div>
                                                                        <BetLogo bet={player} className="sport-logos-pending" alt="" />
                                                                    <span className="fw-bold team-color-pending">
                                                                        {getBetPrimaryName(player)}
                                                                        {liveLabel && ` (${liveLabel})`}
                                                                        {player.odd && (player.odd.points != null || player.odd.price != null) && (
                                                                            <span className="body-odds">

                                                                                {player.odd.points != null
                                                                                    ? formatNumber(player.odd.points, player.odd.selection_line, "points")
                                                                                    : ""
                                                                                }{" "}
                                                                                {player.odd.price != null
                                                                                    ? formatNumber(player.odd.price, player.odd.selection_line, "price")
                                                                                    : ""
                                                                                }
                                                                            </span>
                                                                        )}
                                                                        {isFreePlayBet(player) && (
                                                                            <span className="bet-free">
                                                                                {player.freeBet}
                                                                            </span>
                                                                        )}

                                                                    </span>
                                                                    {getPendingMarketSubLabel(player) && (
                                                                        <div>
                                                                            <span className='name-pending-props'>{getPendingMarketSubLabel(player)}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </span>

                                                        ) : (
                                                            <span className='d-flex'>
                                                                <div>
                                                                    <>
                                                                            <BetLogo bet={player} className="sport-logos-pending" alt="" />
                                                                        <span className='fw-bold team-color-pending'>
                                                                            {getBetPrimaryName(player)}
                                                                        </span>
                                                                    </>

                                                                    &nbsp;
                                                                    {player.gameType === "live" && (
                                                                        <span className='fw-bold'>
                                                                            ({player.gameType})
                                                                        </span>
                                                                    )}
                                                                    <span className="bet-format">
                                                                        {market.toLowerCase().includes("moneyline") || points == null
                                                                            ? `ML ${formatWithSign(price)}`
                                                                            : `${points != null
                                                                                ? formatWithSign(points, selectionLine, "points") + " "
                                                                                : ""
                                                                            }${formatWithSign(price)}`
                                                                        }
                                                                        &nbsp;

                                                                        {isFreePlayBet(player) && (
                                                                            <span className="bet-free">
                                                                                {player.freeBet}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </span>

                                                        )}
                                                    </span>
                                                </td>
                                                <td className='negative'>
                                                    {formatNumbers(player.bet)}
                                                </td>
                                                <td className='none'>
                                                    {formatNumbers(player.reward)}
                                                </td>
                                                <td className='none'>
                                                    {currentUser?.status !== 'Read-Only' &&
                                                        currentUser?.status !== 'Disabled' && (
                                                            <IoCloseSharp
                                                                className="close-icon-pending inter"
                                                                data-bs-toggle="modal"
                                                                data-bs-target="#deletBetUserPending"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedBet(player);
                                                                }}
                                                            />
                                                        )}
                                                </td>
                                            </tr>
                                            {expanded && (
                                                isParlay ? (
                                                    <>
                                                        <tr>
                                                            <td colSpan={5} className="detail">
                                                                <table className='auto-margin'>
                                                                    <tbody className="player-info-details">

                                                                        <tr>
                                                                            <th className="title-table-pending">Ticket#</th>
                                                                            <td className="body-table-pending">{player.ticket}</td>
                                                                        </tr>

                                                                        <tr>
                                                                            <th className="title-table-pending">Status</th>
                                                                            <td className="body-table-pending">
                                                                                {capitalize(player.betStatus)}
                                                                            </td>
                                                                        </tr>

                                                                        <tr>
                                                                            <th className="title-table-pending">Risk / Win</th>
                                                                            <td className="body-table-pending">
                                                                                ${formatNumbers(player.bet)} / ${formatNumbers(player.reward)}
                                                                            </td>
                                                                        </tr>

                                                                        <tr>
                                                                            <th className="title-table-pending">Accepted (PST)</th>
                                                                            <td className="body-table-pending">
                                                                                {formatDateTime(player.betDate)}
                                                                            </td>
                                                                        </tr>

                                                                    </tbody>
                                                                </table>

                                                            </td>
                                                        </tr>
                                                        {player.selections?.map((selection: any, index: number) => (
                                                            <tr key={index}>
                                                                <td colSpan={5} className="detail">
                                                                    <div className="alin-text-pending">
                                                                            <BetLogo bet={selection} className="sport-logos-pending" alt="" />

                                                                        <span className="info-item">
                                                                            {getBetPrimaryName(selection)}
                                                                        </span>

                                                                        <span className="bet-format">

                                                                            {selection.odd?.points != null
                                                                                ? `${formatWithSign(
                                                                                    selection.odd.points,
                                                                                    selection.odd?.selection_line,
                                                                                    "points"
                                                                                )} `
                                                                                : ""
                                                                            }

                                                                            {selection.odd?.price != null
                                                                                ? formatWithSign(selection.odd.price)
                                                                                : ""
                                                                            }

                                                                        </span>
                                                                    </div>
                                                                    <table className='auto-margin'>
                                                                        <tbody className="player-info-details">
                                                                            <tr>
                                                                                <th className="title-table-pending">Game</th>
                                                                                <td className="body-table-pending">
                                                                                    {getBetMatchupLabel(selection)}
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <th className="title-table-pending">Sport</th>
                                                                                <td className="body-table-pending">{selection.sport}</td>
                                                                            </tr>

                                                                            <tr>
                                                                                <th className="title-table-pending">Selection</th>
                                                                                <td className="body-table-pending">
                                                                                    {getAdminPendingSelectionLabel(selection)}
                                                                                </td>
                                                                            </tr>
                                                                            <MlbPitchersDetailRow pitchers={selection.pitchers} />
                                                                            <tr>
                                                                                <th className="title-table-pending">Scheduled (PST)</th>
                                                                                <td className="body-table-pending">
                                                                                    {formatDateTime(selection.gameDate)}
                                                                                </td>
                                                                            </tr>

                                                                            <tr>
                                                                                <th className="title-table-pending">Status</th>
                                                                                <td className="body-table-pending">
                                                                                    {capitalize(selection.betStatus)}
                                                                                </td>
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>
                                                                </td>
                                                            </tr>

                                                        ))}

                                                    </>
                                                ) : (
                                                    <tr>
                                                        <td colSpan={5} className="detail">
                                                            <table className='auto-margin'>
                                                                <tbody className="player-info-details">

                                                                    <tr>
                                                                        <th className="title-table-pending">Ticket#</th>
                                                                        <td className="body-table-pending">{player.ticket}</td>
                                                                    </tr>

                                                                    <tr>
                                                                        <th className="title-table-pending">Status</th>
                                                                        <td className="body-table-pending">
                                                                            {capitalize(player.betStatus)}
                                                                        </td>
                                                                    </tr>

                                                                    <tr>
                                                                        <th className="title-table-pending">Game</th>
                                                                        <td className="body-table-pending">
                                                                            {getBetMatchupLabel(player)}
                                                                        </td>
                                                                    </tr>
                                                                    <tr>
                                                                        <th className="title-table-pending">Sport</th>
                                                                        <td className="body-table-pending">{player.sport}</td>
                                                                    </tr>

                                                                    <tr>
                                                                        <th className="title-table-pending">Risk / Win</th>
                                                                        <td className="body-table-pending">
                                                                            ${formatNumbers(player.bet)} / ${formatNumbers(player.reward)}
                                                                        </td>
                                                                    </tr>

                                                                    <tr>
                                                                        <th className="title-table-pending">Selection</th>
                                                                        <td className="body-table-pending">
                                                                            {getAdminPendingSelectionLabel(player)}
                                                                        </td>
                                                                    </tr>
                                                                    <MlbPitchersDetailRow pitchers={player.pitchers} />
                                                                    <tr>
                                                                        <th className="title-table-pending">Scheduled (PST)</th>
                                                                        <td className="body-table-pending">
                                                                            {formatDateTime(player.gameDate)}
                                                                        </td>
                                                                    </tr>

                                                                    <tr>
                                                                        <th className="title-table-pending">Accepted (PST)</th>
                                                                        <td className="body-table-pending">
                                                                            {formatDateTime(player.betDate)}
                                                                        </td>
                                                                    </tr>

                                                                </tbody>
                                                            </table>

                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </React.Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                </div>
            )}

            <div className="modal fade" id="deletBetUserPending" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title fs-5">
                                <span>Do you want to remove bet?</span>
                            </h4>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <div className="modal-body">
                            <div className="d-flex gap-2 align-items-center">
                                <span className="transactionManager2 text-dark fw-semibold">Bet:</span>
                                <span className="transactionManager2 text-dark fw-semibold">
                                    {selectedBet && hasBetName(selectedBet) && selectedBet.betName}
                                    {selectedBet && isParlay(selectedBet) && (
                                        <div className="parlay-lines">
                                            {selectedBet.selections?.map((selection, index) => (
                                                <div key={index}>{selection.betName}</div>
                                            ))}
                                        </div>
                                    )}
                                </span>
                            </div>
                        </div>
                        <div className="modal-footer d-flex justify-content-between">
                            <button type="button" className="btn btn-outline-danger" data-bs-dismiss="modal">Cancel</button>
                            <button
                                type="button"
                                className="btn btn-outline-success"
                                onClick={confirmDelete}
                                data-bs-dismiss="modal"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default UserPending;
